import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import usePartnerRequestUpdates from '../hooks/usePartnerRequestUpdates';
import {
    buildPartnerGoogleCalendarUrl,
    formatPartnerMoney,
    formatPartnerScheduledAppointment,
    getPartnerPreferredAppointment,
    getPartnerRepairRequest,
    getPartnerStatusLabel,
    schedulePartnerRepairRequest,
    sendPartnerRepairRequestMessage,
    updatePartnerEstimateLineAuthorizations,
    updatePartnerRepairRequestStatus,
} from '../utils/partnerShopApi';
import { getPartnerRequestTitle } from '../utils/partnerRequestDisplay';
import { getUserDetailsBaseUrl } from '../utils/chatIdentity';
import { tryEnableWebPushQuietly } from '../utils/webPush';
import PartnerRequestMessages from './PartnerRequestMessages';
import { GarageProgressConversation } from './chat/GarageProgressSummary';
import AppointmentSlotPicker from './AppointmentSlotPicker';

const DEFAULT_PARTNER_SHOP_TIMEZONE = 'America/Denver';
const TERMINAL_CHAT_STATUSES = new Set(['failed', 'cancelled', 'picked_up', 'completed']);

function Icon({ children, className = '' }) {
    return (
        <svg className={className} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            {children}
        </svg>
    );
}

function getRequestTitle(request) {
    return getPartnerRequestTitle(request, 'Garage repair request');
}

function getVehicleName(request) {
    const vehicle = request?.vehicle || {};
    const parts = [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean);
    return parts.length ? parts.join(' ') : request?.shop_ready_summary?.vehicleName || 'Vehicle';
}

function getStatusTone(status) {
    if (['ready_for_pickup', 'picked_up', 'estimate_accepted', 'scheduled'].includes(status)) {
        return 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]';
    }
    if (['estimate_sent', 'in_service'].includes(status)) {
        return 'border-[#dfd0ff] bg-[#f7f2ff] text-[#6f3cc3]';
    }
    if (['declined', 'failed'].includes(status)) {
        return 'border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]';
    }
    return 'border-[#d9d9d9] bg-white text-[#414141]';
}

function getDeclineReason(request) {
    const notes = String(request?.notes || '').trim();
    if (!notes || /^Customer requested an estimate/i.test(notes)) return '';
    return notes;
}

const ESTIMATE_AUTHORIZATION_LABELS = {
    pending_customer_approval: 'Pending Customer Approval',
    partially_approved_additional_required: 'Partially Approved – Additional Approval Required',
    partially_approved: 'Partially Approved',
    accepted: 'Accepted',
    declined: 'Declined',
};

function getEstimateAuthorizationSummary(estimate) {
    const summary = estimate?.authorization_summary;
    if (summary?.line_count != null) {
        return {
            status: summary.status || 'pending_customer_approval',
            label: summary.label || ESTIMATE_AUTHORIZATION_LABELS[summary.status] || 'Pending Customer Approval',
            authorized_amount: Number(summary.authorized_amount || 0),
            additional_approval_amount: Number(summary.additional_approval_amount || summary.pending_amount || 0),
            declined_amount: Number(summary.declined_amount || 0),
            proposed_total: Number(summary.proposed_total ?? estimate?.total_amount ?? 0),
            accepted_count: Number(summary.accepted_count || 0),
            pending_count: Number(summary.pending_count || 0),
            declined_count: Number(summary.declined_count || 0),
            line_count: Number(summary.line_count || 0),
        };
    }

    const status = estimate?.estimate_status === 'accepted'
        ? 'accepted'
        : estimate?.estimate_status === 'declined'
        ? 'declined'
        : 'pending_customer_approval';
    const total = Number(estimate?.total_amount || 0);
    return {
        status,
        label: ESTIMATE_AUTHORIZATION_LABELS[status],
        authorized_amount: status === 'accepted' ? total : 0,
        additional_approval_amount: status === 'pending_customer_approval' ? total : 0,
        declined_amount: status === 'declined' ? total : 0,
        proposed_total: total,
        accepted_count: status === 'accepted' ? 1 : 0,
        pending_count: status === 'pending_customer_approval' ? 1 : 0,
        declined_count: status === 'declined' ? 1 : 0,
        line_count: estimate ? 1 : 0,
    };
}

function getLineAuthorizationStatus(line) {
    const status = String(line?.authorization_status || 'pending').toLowerCase();
    return ['accepted', 'declined', 'pending'].includes(status) ? status : 'pending';
}

function getLineAuthorizationLabel(status) {
    if (status === 'accepted') return 'Accepted';
    if (status === 'declined') return 'Declined';
    return '';
}

function getLineAuthorizationClass(status) {
    if (status === 'accepted') return 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]';
    if (status === 'declined') return 'border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]';
    return 'border-[#f7d58f] bg-[#fff8e8] text-[#936000]';
}

function getVisibleLineAuthorizationNote(line, request) {
    const lineNote = String(line?.authorization_note || '').trim();
    if (!lineNote) return '';

    // A shop declines a lead at the request level. Older API responses copied that
    // request-level reason onto every pending estimate line, which made the same
    // explanation appear beside every item. Keep genuine line-specific notes, but
    // show a duplicated lead reason only once in the estimate's Decline note card.
    const requestNote = String(request?.notes || '').trim();
    if (String(request?.request_status || '').toLowerCase() === 'declined'
        && requestNote
        && lineNote === requestNote) {
        return '';
    }
    return lineNote;
}

function getEstimateAuthorizationUnits(lines = []) {
    const units = [];
    const grouped = new Map();
    lines.forEach((line, index) => {
        const groupKey = String(line?.approval_group_key || '').trim();
        if (!groupKey) {
            units.push({
                key: line?.estimate_line_id || `line-${index}`,
                isGroup: false,
                label: '',
                lines: [line],
                firstIndex: index,
            });
            return;
        }
        if (!grouped.has(groupKey)) {
            const unit = {
                key: `group-${groupKey}`,
                isGroup: true,
                label: line?.approval_group_label || 'Required together',
                lines: [],
                firstIndex: index,
            };
            grouped.set(groupKey, unit);
            units.push(unit);
        }
        grouped.get(groupKey).lines.push(line);
    });
    return units.sort((a, b) => a.firstIndex - b.firstIndex);
}

function getShopTimezone(request) {
    return request?.partner_shop?.timezone || request?.scheduled_timezone || DEFAULT_PARTNER_SHOP_TIMEZONE;
}

function formatTimezoneLabel(timezone) {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone || DEFAULT_PARTNER_SHOP_TIMEZONE,
            timeZoneName: 'short',
        }).formatToParts(new Date());
        return parts.find((part) => part.type === 'timeZoneName')?.value || timezone || 'local time';
    } catch (_) {
        return timezone || 'local time';
    }
}

function getCalendarStatus(calendar) {
    const status = String(calendar?.sync_status || '').toLowerCase();
    if (status === 'synced') {
        return { label: 'Google Calendar synced', tone: 'success' };
    }
    if (status === 'failed') {
        return { label: 'Google Calendar sync failed', tone: 'danger' };
    }
    return { label: '', tone: 'muted' };
}

const getDatePartsForTimezone = (timezone) => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone || DEFAULT_PARTNER_SHOP_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(new Date());
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return {
        year: Number(values.year),
        month: Number(values.month),
        day: Number(values.day),
    };
};

// Today's date (YYYY-MM-DD) in the shop's timezone — the earliest bookable day, so same-day
// drop-offs are allowed. The backend still drops any already-passed time (plus the notice
// window), so today only ever shows genuinely future slots.
const getShopTodayISO = (timezone = DEFAULT_PARTNER_SHOP_TIMEZONE) => {
    const t = getDatePartsForTimezone(timezone);
    return `${t.year}-${String(t.month).padStart(2, '0')}-${String(t.day).padStart(2, '0')}`;
};

const getDateISOForTimezone = (value, timezone = DEFAULT_PARTNER_SHOP_TIMEZONE) => {
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return '';
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(date);
        const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
        return `${values.year}-${values.month}-${values.day}`;
    } catch (_) {
        return String(value || '').slice(0, 10);
    }
};

function ScheduleAppointmentCard({ request, onScheduled }) {
    const timezone = getShopTimezone(request);
    const todayISO = useMemo(() => getShopTodayISO(timezone), [timezone]);
    const preferredAppointment = useMemo(() => getPartnerPreferredAppointment(request), [request]);
    const shopId = request?.partner_shop_id || request?.partner_shop?.id || null;
    const [selectedDate, setSelectedDate] = useState(todayISO);
    const [selectedTime, setSelectedTime] = useState('');
    const [notes, setNotes] = useState('');
    const [isScheduling, setIsScheduling] = useState(false);
    const [error, setError] = useState('');
    // Bumped to force the slot grid to refetch after a lost booking race.
    const [reloadToken, setReloadToken] = useState(0);

    useEffect(() => {
        const usePreferred = preferredAppointment?.date && preferredAppointment.date >= todayISO;
        setSelectedDate(usePreferred ? preferredAppointment.date : todayISO);
        setSelectedTime(usePreferred ? preferredAppointment.time : '');
        setNotes('');
        setError('');
    }, [request?.request_id, todayISO, preferredAppointment?.date, preferredAppointment?.time]);

    const handleSchedule = async () => {
        if (!selectedDate || !selectedTime || isScheduling) return;
        setIsScheduling(true);
        setError('');
        try {
            const updated = await schedulePartnerRepairRequest(request.request_id, {
                scheduled_date: selectedDate,
                scheduled_time: selectedTime,
                timezone,
                duration_minutes: 15,
                notes,
            });
            if (updated) onScheduled(updated);
        } catch (scheduleError) {
            // 409 = the slot filled between fetch and confirm — clear + refresh + ask again.
            if (Number(scheduleError?.status) === 409) {
                setSelectedTime('');
                setReloadToken((t) => t + 1);
                setError('That time was just booked. Please pick another slot.');
            } else {
                setError(scheduleError?.message || 'Could not schedule this appointment.');
            }
        } finally {
            setIsScheduling(false);
        }
    };

    return (
        <section className="rounded-2xl border border-[#d9d9d9] bg-white p-4 shadow-sm">
            <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f6f6f6] text-[#414141]">
                    <Icon><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18" /></Icon>
                </span>
                <div>
                    <p className="text-base font-black text-[#414141]">Schedule a shop visit</p>
                    <p className="mt-1 text-sm font-semibold text-[#414141]">Available times at {request.partner_shop?.name || 'the shop'}.</p>
                </div>
            </div>

            <div className="mt-4">
                <AppointmentSlotPicker
                    shopId={shopId}
                    minDate={todayISO}
                    selectedDate={selectedDate}
                    onSelectDate={(d) => { setSelectedDate(d); setSelectedTime(''); setError(''); }}
                    selectedTime={selectedTime}
                    onSelectSlot={(d, t) => { setSelectedDate(d); setSelectedTime(t); setError(''); }}
                    reloadToken={reloadToken}
                    disabled={isScheduling}
                />
            </div>
            {preferredAppointment?.label && (
                <p className="mt-2 rounded-xl border border-[#d9d9d9] bg-white px-3 py-2 text-xs font-bold text-[#414141]">
                    Pre-filled from shop intake: {preferredAppointment.label}. Choose another open time if needed.
                </p>
            )}

            <textarea
                value={notes}
                onChange={(event) => setNotes(event.target.value.slice(0, 600))}
                rows={3}
                placeholder="Anything the shop should know before drop-off?"
                className="mt-3 w-full resize-none rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 py-2 text-sm font-semibold text-[#414141] outline-none transition focus:border-[#D81F26] focus:bg-white"
            />

            <div className="mt-4 flex flex-col gap-3 border-t border-[#ededed] pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs font-semibold text-[#414141]">Times shown in {formatTimezoneLabel(timezone)}</p>
                <button
                    type="button"
                    onClick={handleSchedule}
                    disabled={isScheduling || !selectedDate || !selectedTime}
                    className="inline-flex h-10 items-center justify-center rounded-xl bg-[#D81F26] px-5 text-sm font-black text-white hover:bg-[#B8181E] disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {isScheduling ? 'Scheduling...' : 'Confirm appointment'}
                </button>
            </div>
            {error && <p className="mt-3 rounded-xl border border-[#ffd2d2] bg-[#fff1f1] px-3 py-2 text-sm font-bold text-[#b42318]">{error}</p>}
        </section>
    );
}

function RescheduleAppointmentCard({
    request,
    rescheduleRequest = null,
    onScheduled,
    initialExpanded = false,
    onClose,
}) {
    const timezone = getShopTimezone(request);
    const todayISO = useMemo(() => getShopTodayISO(timezone), [timezone]);
    const currentDate = getDateISOForTimezone(request?.scheduled_start_at, timezone);
    const shopId = request?.partner_shop_id || request?.partner_shop?.id || null;
    const [expanded, setExpanded] = useState(initialExpanded);
    const [selectedDate, setSelectedDate] = useState(currentDate && currentDate >= todayISO ? currentDate : todayISO);
    const [selectedTime, setSelectedTime] = useState('');
    const [notes, setNotes] = useState('');
    const [isScheduling, setIsScheduling] = useState(false);
    const [error, setError] = useState('');
    const [reloadToken, setReloadToken] = useState(0);

    useEffect(() => {
        const nextCurrentDate = getDateISOForTimezone(request?.scheduled_start_at, timezone);
        setExpanded(initialExpanded);
        setSelectedDate(nextCurrentDate && nextCurrentDate >= todayISO ? nextCurrentDate : todayISO);
        setSelectedTime('');
        setNotes('');
        setError('');
        setReloadToken(0);
    }, [initialExpanded, request?.request_id, request?.scheduled_start_at, timezone, todayISO]);

    const handleSchedule = async () => {
        if (!request?.request_id || !selectedDate || !selectedTime || isScheduling) return;
        setIsScheduling(true);
        setError('');
        try {
            const updated = await schedulePartnerRepairRequest(request.request_id, {
                scheduled_date: selectedDate,
                scheduled_time: selectedTime,
                timezone,
                duration_minutes: 15,
                notes,
            });
            if (updated) onScheduled?.(updated);
            onClose?.();
        } catch (scheduleError) {
            if (Number(scheduleError?.status) === 409) {
                setSelectedTime('');
                setReloadToken((t) => t + 1);
                setError((scheduleError?.message || '').replace(/^(SLOT_TAKEN|CLOSED):\s*/, '') || 'That time is unavailable. Please pick another.');
            } else {
                setError(scheduleError?.message || 'Could not reschedule this appointment.');
            }
        } finally {
            setIsScheduling(false);
        }
    };

    const appointmentLabel = formatPartnerScheduledAppointment(request);
    const reason = String(rescheduleRequest?.reason || '').trim();
    const isShopRequest = Boolean(rescheduleRequest);

    return (
        <section className="rounded-2xl border border-[#f7d58f] bg-[#fff8e8] p-4 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-3 text-[#936000]">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white">
                        <Icon className="h-4 w-4"><path d="M8 2v4M16 2v4M3 10h18" /><rect x="3" y="4" width="18" height="18" rx="2" /><path d="m16 14-4 4-4-4" /></Icon>
                    </span>
                    <div>
                        <p className="text-xs font-black uppercase tracking-[0.08em]">
                            {isShopRequest ? 'Reschedule requested' : 'Reschedule appointment'}
                        </p>
                        <p className="mt-1 text-base font-black text-[#414141]">
                            {isShopRequest ? `${request?.partner_shop?.name || 'The shop'} is asking for a new time` : 'Choose a new drop-off time'}
                        </p>
                        <p className="mt-1 text-sm font-semibold leading-relaxed text-[#414141]">
                            Current appointment{appointmentLabel ? `: ${appointmentLabel}` : ' is scheduled'}.
                        </p>
                        {reason && (
                            <p className="mt-2 whitespace-pre-wrap break-words rounded-xl border border-[#f7d58f] bg-white px-3 py-2 text-sm font-semibold text-[#414141]">
                                {reason}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                    <button
                        type="button"
                        onClick={() => setExpanded((value) => !value)}
                        className="inline-flex min-h-[34px] items-center rounded-full bg-[#414141] px-4 text-xs font-black text-white transition hover:bg-[#2A2A2A]"
                    >
                        {expanded ? 'Hide times' : 'Pick new time'}
                    </button>
                    {onClose && (
                        <button
                            type="button"
                            onClick={onClose}
                            className="inline-flex min-h-[34px] items-center rounded-full border border-[#d9d9d9] bg-white px-3 text-xs font-black text-[#414141]"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </div>
            {expanded && (
                <div className="mt-4 border-t border-[#f7d58f] pt-4">
                    <AppointmentSlotPicker
                        shopId={shopId}
                        minDate={todayISO}
                        selectedDate={selectedDate}
                        onSelectDate={(d) => { setSelectedDate(d); setSelectedTime(''); setError(''); }}
                        selectedTime={selectedTime}
                        onSelectSlot={(d, t) => { setSelectedDate(d); setSelectedTime(t); setError(''); }}
                        reloadToken={reloadToken}
                        disabled={isScheduling}
                    />
                    <textarea
                        value={notes}
                        onChange={(event) => setNotes(event.target.value.slice(0, 600))}
                        rows={3}
                        placeholder="Optional note for the shop"
                        className="mt-3 w-full resize-none rounded-xl border border-[#f7d58f] bg-white px-3 py-2 text-sm font-semibold text-[#414141] outline-none transition focus:border-[#D81F26]"
                    />
                    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs font-semibold text-[#414141]">Times shown in {formatTimezoneLabel(timezone)}</p>
                        <button
                            type="button"
                            onClick={handleSchedule}
                            disabled={isScheduling || !selectedDate || !selectedTime}
                            className="inline-flex h-10 items-center justify-center rounded-xl bg-[#D81F26] px-5 text-sm font-black text-white hover:bg-[#B8181E] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {isScheduling ? 'Rescheduling...' : 'Confirm new time'}
                        </button>
                    </div>
                    {error && <p className="mt-3 rounded-xl border border-[#ffd2d2] bg-[#fff1f1] px-3 py-2 text-sm font-bold text-[#b42318]">{error}</p>}
                </div>
            )}
        </section>
    );
}

function shouldShowVisitReminder(request) {
    return Boolean(request?.scheduled_start_at) && String(request?.request_status || '').toLowerCase() === 'scheduled';
}

function getDriverVisitUpdateMessage(request = {}) {
    const shopName = request?.partner_shop?.name || 'the shop';
    return `I visited ${shopName} for my scheduled appointment.`;
}

function hasDriverVisitUpdateMessage(messages = [], request = {}) {
    const expected = getDriverVisitUpdateMessage(request).toLowerCase();
    return messages.some((message) => (
        message?.sender_role === 'driver' &&
        String(message?.message_text || '').trim().toLowerCase() === expected
    ));
}

function VisitReminderCard({
    request,
    addToCalendarUrl = '',
    onConfirmVisit,
    isSubmitting = false,
    visitUpdateSent = false,
    notice = '',
    actionsEnabled = true,
    onReschedule,
}) {
    const shopName = request?.partner_shop?.name || 'the shop';
    const address = request?.partner_shop?.address || '';
    const directionsUrl = address ? `https://maps.google.com/?q=${encodeURIComponent(address)}` : '';
    const appointmentLabel = formatPartnerScheduledAppointment(request);
    const failed = /could not|failed|unable/i.test(notice);

    return (
        <section className="rounded-2xl border border-[#f7d58f] bg-[#fff8e8] p-4 shadow-sm">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-3 text-[#936000]">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white">
                        <Icon className="h-4 w-4"><path d="M8 2v4M16 2v4M3 10h18" /><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M9 16l2 2 4-5" /></Icon>
                    </span>
                    <div>
                        <p className="text-xs font-black uppercase tracking-[0.08em]">Scheduled service</p>
                        <p className="mt-1 text-base font-black text-[#414141]">Did you visit {shopName}?</p>
                        <p className="mt-1 text-sm font-semibold leading-relaxed text-[#414141]">
                            You have service scheduled{appointmentLabel ? ` for ${appointmentLabel}` : ''}.
                        </p>
                        {address && <p className="mt-1 text-xs font-semibold text-[#414141]">{address}</p>}
                    </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                    {visitUpdateSent || !actionsEnabled ? (
                        <span className="inline-flex min-h-[34px] items-center rounded-full border border-[#bde7d5] bg-white px-3 text-xs font-black text-[#087a43]">
                            {visitUpdateSent ? 'Visit update sent' : 'Scheduled'}
                        </span>
                    ) : (
                        <button
                            type="button"
                            onClick={onConfirmVisit}
                            disabled={isSubmitting}
                            className="inline-flex min-h-[34px] items-center justify-center rounded-full bg-[#D81F26] px-4 text-xs font-black text-white transition hover:bg-[#B8181E] disabled:cursor-wait disabled:opacity-60"
                        >
                            {isSubmitting ? 'Sending...' : 'Yes, tell shop'}
                        </button>
                    )}
                    {directionsUrl && (
                        <a
                            href={directionsUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex min-h-[34px] items-center rounded-full border border-[#d9d9d9] bg-white px-3 text-xs font-black text-[#414141] no-underline transition hover:border-[#D81F26] hover:text-[#D81F26]"
                        >
                            Directions
                        </a>
                    )}
                    {actionsEnabled && onReschedule && (
                        <button
                            type="button"
                            onClick={onReschedule}
                            className="inline-flex min-h-[34px] items-center rounded-full border border-[#f7d58f] bg-white px-3 text-xs font-black text-[#936000] transition hover:bg-[#fff1cf]"
                        >
                            Reschedule
                        </button>
                    )}
                    {addToCalendarUrl && (
                        <a
                            href={addToCalendarUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex min-h-[34px] items-center rounded-full border border-[#d9d9d9] bg-white px-3 text-xs font-black text-[#414141] no-underline transition hover:border-[#D81F26] hover:text-[#D81F26]"
                        >
                            Add to Google Calendar
                        </a>
                    )}
                </div>
            </div>
            {notice && (
                <p className={`mt-3 rounded-xl px-3 py-2 text-sm font-bold ${
                    failed
                        ? 'border border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]'
                        : 'border border-[#bde7d5] bg-white text-[#087a43]'
                }`}>
                    {notice}
                </p>
            )}
        </section>
    );
}

const GARAGE_SERVICE_STATUS_ORDER = [
    'submitted',
    'viewed',
    'accepted',
    'estimate_sent',
    'estimate_accepted',
    'scheduled',
    'in_service',
    'ready_for_pickup',
    'picked_up',
    'completed',
];

const GARAGE_SERVICE_CARD_STATUSES = ['in_service', 'ready_for_pickup'];
const NON_RESCHEDULABLE_APPOINTMENT_STATUSES = new Set([
    'in_service',
    'ready_for_pickup',
    'picked_up',
    'completed',
    'cancelled',
    'failed',
]);

function hasReachedGarageServiceStatus(currentStatus, targetStatus) {
    const currentIndex = GARAGE_SERVICE_STATUS_ORDER.indexOf(String(currentStatus || '').toLowerCase());
    const targetIndex = GARAGE_SERVICE_STATUS_ORDER.indexOf(String(targetStatus || '').toLowerCase());
    return currentIndex >= 0 && targetIndex >= 0 && currentIndex >= targetIndex;
}

function canReschedulePartnerAppointment(request) {
    const status = String(request?.request_status || '').toLowerCase();
    return Boolean(request?.scheduled_start_at) && !NON_RESCHEDULABLE_APPOINTMENT_STATUSES.has(status);
}

function getPendingRescheduleRequest(request = {}) {
    const explicitRequest = (request?.reschedule_requests || [])
        .filter((item) => String(item?.status || '').toLowerCase() === 'pending')
        .slice(-1)[0];
    if (explicitRequest) return explicitRequest;
    return (request?.timeline || [])
        .filter((item) => (
            item?.event === 'reschedule_requested' &&
            String(item?.status || 'pending').toLowerCase() === 'pending'
        ))
        .map((item) => ({
            reschedule_request_id: item.reschedule_request_id || '',
            request_id: request?.request_id || request?.id || '',
            reason: item.reason || '',
            status: item.status || 'pending',
            previous_scheduled_start_at: item.previous_scheduled_start_at || '',
            previous_scheduled_end_at: item.previous_scheduled_end_at || '',
            previous_scheduled_timezone: item.previous_scheduled_timezone || '',
            created_at: item.created_at || '',
        }))
        .slice(-1)[0] || null;
}

function ServicePickupCard({
    request,
    onConfirmPickup,
    isSubmitting = false,
    notice = '',
    statusOverride = '',
    actionsEnabled = true,
}) {
    const status = String(statusOverride || request?.request_status || '').toLowerCase();
    if (!GARAGE_SERVICE_CARD_STATUSES.includes(status)) return null;
    const readyForPickup = status === 'ready_for_pickup';
    const currentStatus = String(request?.request_status || '').toLowerCase();
    const canConfirmPickup = actionsEnabled && readyForPickup && currentStatus === 'ready_for_pickup' && onConfirmPickup;
    const shopName = request?.partner_shop?.name || 'the shop';
    const failed = /could not|not ready|failed|unable/i.test(notice);
    const passiveLabel = readyForPickup && ['picked_up', 'completed'].includes(currentStatus) ? 'Car taken' : 'Waiting for shop';

    return (
        <section className={`rounded-2xl border p-4 shadow-sm ${
            readyForPickup
                ? 'border-[#bde7d5] bg-[#ecfff5]'
                : 'border-[#dfd0ff] bg-[#f7f2ff]'
        }`}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className={`flex items-start gap-3 ${readyForPickup ? 'text-[#087a43]' : 'text-[#6f3cc3]'}`}>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white">
                        <Icon className="h-4 w-4"><path d="M20 6 9 17l-5-5" /></Icon>
                    </span>
                    <div>
                        <p className="text-xs font-black uppercase tracking-[0.08em]">
                            {readyForPickup ? 'Ready for pickup' : 'Vehicle in service'}
                        </p>
                        <p className="mt-1 text-base font-black text-[#414141]">
                            {readyForPickup ? 'Is service complete and car taken?' : 'Service is in progress'}
                        </p>
                        <p className="mt-1 text-sm font-semibold leading-relaxed text-[#414141]">
                            {readyForPickup
                                ? `The shop marked service complete. Confirm after you have taken your car from ${shopName}.`
                                : `Your vehicle is in service at ${shopName}. Once the shop marks it ready, confirm here after you take the car.`
                            }
                        </p>
                    </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                    {canConfirmPickup ? (
                        <button
                            type="button"
                            onClick={onConfirmPickup}
                            disabled={isSubmitting}
                            className="inline-flex min-h-[34px] items-center justify-center rounded-full bg-[#0a8f53] px-4 text-xs font-black text-white transition hover:bg-[#087a43] disabled:cursor-wait disabled:opacity-60"
                        >
                            {isSubmitting ? 'Confirming...' : 'Yes, car taken'}
                        </button>
                    ) : (
                        <span className={`inline-flex min-h-[34px] items-center rounded-full border bg-white px-3 text-xs font-black ${
                            readyForPickup
                                ? 'border-[#bde7d5] text-[#087a43]'
                                : 'border-[#dfd0ff] text-[#6f3cc3]'
                        }`}>
                            {passiveLabel}
                        </span>
                    )}
                </div>
            </div>
            {notice && (
                <p className={`mt-3 rounded-xl px-3 py-2 text-sm font-bold ${
                    failed
                        ? 'border border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]'
                        : 'border border-[#bde7d5] bg-white text-[#087a43]'
                }`}>
                    {notice}
                </p>
            )}
        </section>
    );
}

function EstimateCard({ request, onUpdated }) {
    const [isUpdating, setIsUpdating] = useState(false);
    const [updatingLineId, setUpdatingLineId] = useState('');
    const [notice, setNotice] = useState('');
    // #575: '' | 'accepted' | 'declined' while the Approve all / Decline all confirmation is open.
    const [bulkConfirmDecision, setBulkConfirmDecision] = useState('');
    const [isBulkUpdating, setIsBulkUpdating] = useState(false);
    // State updates are async, so a fast double click can slip past isUpdating; the ref blocks it.
    const decisionInFlightRef = useRef(false);
    const estimate = request?.latest_estimate;
    const status = String(request?.request_status || '');
    const authorizationSummary = getEstimateAuthorizationSummary(estimate);
    const estimateLines = Array.isArray(estimate?.lines) ? estimate.lines : [];
    const authorizationUnits = getEstimateAuthorizationUnits(estimateLines);
    const pendingLines = estimateLines.filter((line) => getLineAuthorizationStatus(line) === 'pending');
    const hasPendingLines = pendingLines.length > 0;
    const estimateAccepted = ['accepted', 'partially_approved'].includes(authorizationSummary.status)
        || ['estimate_accepted', 'scheduled', 'in_service', 'ready_for_pickup', 'picked_up', 'completed'].includes(status);
    const estimateDeclined = authorizationSummary.status === 'declined' || ['declined', 'cancelled', 'failed'].includes(status);
    const canRespond = Boolean(estimate) && hasPendingLines && !['scheduled', 'in_service', 'ready_for_pickup', 'picked_up', 'completed', 'declined', 'cancelled', 'failed'].includes(status);
    const declineReasonText = estimateDeclined ? getDeclineReason(request) : '';
    // #575: bulk actions only ever touch undecided lines, so earlier approvals/declines (including
    // approvals carried forward onto a revised estimate) are never changed.
    const bulkEligibleLines = canRespond ? pendingLines.filter((line) => line?.estimate_line_id) : [];
    const bulkEligibleTotal = bulkEligibleLines.reduce((sum, line) => sum + Number(line.amount || 0), 0);
    const bulkHasPriorDecisions = pendingLines.length < estimateLines.length;

    useEffect(() => {
        setBulkConfirmDecision('');
    }, [request?.request_id, estimate?.estimate_id, status]);

    const updateLineAuthorization = async (linesToUpdate, decision, { bulk = false } = {}) => {
        const validLines = (Array.isArray(linesToUpdate) ? linesToUpdate : [linesToUpdate])
            .filter((line) => line?.estimate_line_id && getLineAuthorizationStatus(line) === 'pending');
        if (!validLines.length || isUpdating || decisionInFlightRef.current) return;
        decisionInFlightRef.current = true;
        setIsUpdating(true);
        setIsBulkUpdating(bulk);
        setUpdatingLineId(validLines.map((line) => line.estimate_line_id).join('|'));
        setNotice('');
        try {
            const updated = await updatePartnerEstimateLineAuthorizations(
                request.request_id,
                validLines.map((line) => ({
                    estimate_line_id: line.estimate_line_id,
                    decision,
                })),
                ''
            );
            if (updated) onUpdated(updated);
            setBulkConfirmDecision('');
            if (bulk) {
                const itemLabel = `${validLines.length} item${validLines.length === 1 ? '' : 's'}`;
                setNotice(decision === 'accepted' ? `Approved ${itemLabel}.` : `Declined ${itemLabel}.`);
            } else {
                setNotice(decision === 'accepted' ? 'Authorization saved.' : 'Decision saved.');
            }
        } catch (error) {
            setNotice(error?.message || 'Could not update this estimate.');
        } finally {
            decisionInFlightRef.current = false;
            setIsUpdating(false);
            setIsBulkUpdating(false);
            setUpdatingLineId('');
        }
    };

    if (!estimate) {
        return (
            <section className="rounded-2xl border border-[#f7d58f] bg-[#fff8e8] p-4 shadow-sm">
                <p className="text-sm font-black text-[#936000]">Waiting for shop estimate</p>
                <p className="mt-1 text-sm font-semibold leading-relaxed text-[#936000]/80">
                    The shop has the symptom report. Messages and updates for this request will stay in this shop conversation.
                </p>
            </section>
        );
    }

    return (
        <section className="rounded-2xl border border-[#dfd0ff] bg-[#f7f2ff] p-4 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#6f3cc3]">Itemized Estimate</p>
                    <p className="mt-1 text-2xl font-black text-[#414141]">
                        {formatPartnerMoney(authorizationSummary.proposed_total, estimate.currency)}
                    </p>
                    <span className={`mt-2 inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-black ${getLineAuthorizationClass(hasPendingLines ? 'pending' : estimateDeclined ? 'declined' : 'accepted')}`}>
                        {authorizationSummary.label}
                    </span>
                </div>
                {!canRespond && estimateAccepted && (
                    <span className="inline-flex items-center gap-2 rounded-xl border border-[#bde7d5] bg-[#ecfff5] px-3 py-2 text-xs font-black text-[#087a43]">
                        <Icon className="h-3.5 w-3.5"><path d="M20 6 9 17l-5-5" /></Icon>
                        {authorizationSummary.label}
                    </span>
                )}
                {!canRespond && estimateDeclined && (
                    <span className="inline-flex items-center gap-2 rounded-xl border border-[#ffd2d2] bg-[#fff1f1] px-3 py-2 text-xs font-black text-[#b42318]">
                        <Icon className="h-3.5 w-3.5"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></Icon>
                        Estimate declined
                    </span>
                )}
            </div>
            <div className="mt-4 grid gap-2 sm:grid-cols-3">
                <div className="rounded-xl border border-[#d9d9d9] bg-white px-3 py-2">
                    <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#414141]">Already authorized</p>
                    <p className="mt-1 text-base font-black text-[#087a43]">{formatPartnerMoney(authorizationSummary.authorized_amount, estimate.currency)}</p>
                </div>
                <div className="rounded-xl border border-[#f7d58f] bg-white px-3 py-2">
                    <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#414141]">Awaiting approval</p>
                    <p className="mt-1 text-base font-black text-[#936000]">{formatPartnerMoney(authorizationSummary.additional_approval_amount, estimate.currency)}</p>
                </div>
                <div className="rounded-xl border border-[#d9d9d9] bg-white px-3 py-2">
                    <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#414141]">Proposed total</p>
                    <p className="mt-1 text-base font-black text-[#414141]">{formatPartnerMoney(authorizationSummary.proposed_total, estimate.currency)}</p>
                </div>
            </div>
            {authorizationSummary.declined_amount > 0 && (
                <p className="mt-2 text-xs font-black text-[#b42318]">
                    Declined line items: {formatPartnerMoney(authorizationSummary.declined_amount, estimate.currency)}
                </p>
            )}
            {estimate.message && (
                <p className="mt-3 text-sm font-semibold leading-relaxed text-[#414141]">{estimate.message}</p>
            )}
            <div className="mt-3 divide-y divide-[#e8defc]">
                {authorizationUnits.map((unit) => {
                    const pendingUnitLines = unit.lines.filter((line) => getLineAuthorizationStatus(line) === 'pending' && line?.estimate_line_id);
                    const unitBusy = isUpdating && pendingUnitLines.some((line) => updatingLineId.split('|').includes(line.estimate_line_id));
                    if (unit.isGroup) {
                        const groupTotal = unit.lines.reduce((sum, line) => sum + Number(line.amount || 0), 0);
                        return (
                            <div key={unit.key} className="py-2">
                                <div className="rounded-xl border border-[#d9d9d9] bg-white p-3">
                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                        <div className="min-w-0">
                                            <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#414141]">Grouped approval</p>
                                            <p className="mt-0.5 text-sm font-black text-[#414141]">{unit.label}</p>
                                        </div>
                                        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                                            <span className="text-sm font-black text-[#414141]">{formatPartnerMoney(groupTotal, estimate.currency)}</span>
                                            {canRespond && pendingUnitLines.length > 0 && (
                                                <>
                                                    <button
                                                        type="button"
                                                        disabled={isUpdating}
                                                        onClick={() => updateLineAuthorization(pendingUnitLines, 'accepted')}
                                                        className="inline-flex h-8 items-center rounded-lg bg-[#0a8f53] px-3 text-[11px] font-black text-white transition hover:bg-[#087a43] disabled:cursor-wait disabled:opacity-60"
                                                    >
                                                        {unitBusy ? 'Saving...' : 'Approve group'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={isUpdating}
                                                        onClick={() => updateLineAuthorization(pendingUnitLines, 'declined')}
                                                        className="inline-flex h-8 items-center rounded-lg border border-[#ffd2d2] bg-white px-3 text-[11px] font-black text-[#b42318] transition hover:bg-[#fff1f1] disabled:cursor-wait disabled:opacity-60"
                                                    >
                                                        Decline group
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-2 divide-y divide-[#d9d9d9]">
                                        {unit.lines.map((line) => {
                                            const lineStatus = getLineAuthorizationStatus(line);
                                            return (
                                                <div key={line.estimate_line_id || line.description} className="flex items-center justify-between gap-3 py-1.5">
                                                    <div className="min-w-0">
                                                        <span className="text-sm font-bold text-[#414141]">{line.description}</span>
                                                        {lineStatus !== 'pending' && (
                                                            <span className={`ml-2 rounded-full border px-2 py-0.5 text-[10px] font-black ${getLineAuthorizationClass(lineStatus)}`}>
                                                                {getLineAuthorizationLabel(lineStatus)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="shrink-0 text-sm font-black text-[#414141]">{formatPartnerMoney(line.amount, estimate.currency)}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        );
                    }
                    const line = unit.lines[0];
                    const lineStatus = getLineAuthorizationStatus(line);
                    const lineAuthorizationNote = getVisibleLineAuthorizationNote(line, request);
                    const lineBusy = isUpdating && updatingLineId === line.estimate_line_id;
                    const lineCanRespond = canRespond && lineStatus === 'pending' && line.estimate_line_id;
                    return (
                        <div key={line.estimate_line_id || line.description} className="flex flex-col gap-2 py-2 text-sm font-bold sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0">
                                <span className="text-[#414141]">{line.description}</span>
                                {(lineStatus !== 'pending' || line.authorization_note) && (
                                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                        {lineStatus !== 'pending' && (
                                            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-black ${getLineAuthorizationClass(lineStatus)}`}>
                                                {getLineAuthorizationLabel(lineStatus)}
                                            </span>
                                        )}
                                        {line.authorization_note && (
                                            <span className="text-[10px] font-semibold text-[#414141]">{line.authorization_note}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="flex shrink-0 items-center justify-between gap-2 sm:justify-end">
                                <span className="text-[#414141]">{formatPartnerMoney(line.amount, estimate.currency)}</span>
                                {lineCanRespond && (
                                    <div className="flex shrink-0 items-center gap-1.5">
                                        <button
                                            type="button"
                                            disabled={isUpdating}
                                            onClick={() => updateLineAuthorization(line, 'accepted')}
                                            className="inline-flex h-8 items-center rounded-lg bg-[#0a8f53] px-3 text-[11px] font-black text-white transition hover:bg-[#087a43] disabled:cursor-wait disabled:opacity-60"
                                        >
                                            {lineBusy ? 'Saving...' : 'Approve'}
                                        </button>
                                        <button
                                            type="button"
                                            disabled={isUpdating}
                                            onClick={() => updateLineAuthorization(line, 'declined')}
                                            className="inline-flex h-8 items-center rounded-lg border border-[#ffd2d2] bg-white px-3 text-[11px] font-black text-[#b42318] transition hover:bg-[#fff1f1] disabled:cursor-wait disabled:opacity-60"
                                        >
                                            Decline
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            {/* #575: full-estimate actions. Hidden when nothing is left to decide. */}
            {bulkEligibleLines.length > 0 && (
                <div className="mt-3 border-t border-[#e8defc] pt-3">
                    {bulkConfirmDecision ? (
                        <div
                            role="alertdialog"
                            aria-label={bulkConfirmDecision === 'accepted' ? 'Confirm approve all' : 'Confirm decline all'}
                            className={`rounded-xl border bg-white p-3 ${bulkConfirmDecision === 'accepted' ? 'border-[#bde7d5]' : 'border-[#ffd2d2]'}`}
                        >
                            <p className="text-sm font-black text-[#182233]">
                                {bulkConfirmDecision === 'accepted' ? 'Approve' : 'Decline'} {bulkEligibleLines.length} remaining item{bulkEligibleLines.length === 1 ? '' : 's'} totaling {formatPartnerMoney(bulkEligibleTotal, estimate.currency)}?
                            </p>
                            {bulkHasPriorDecisions && (
                                <p className="mt-1 text-xs font-semibold text-[#5f728a]">
                                    Items you already approved or declined will not change.
                                </p>
                            )}
                            {bulkConfirmDecision === 'declined' && !estimateLines.some((line) => getLineAuthorizationStatus(line) === 'accepted') && (
                                <p className="mt-1 text-xs font-semibold text-[#5f728a]">This will close the estimate.</p>
                            )}
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    disabled={isUpdating}
                                    onClick={() => updateLineAuthorization(bulkEligibleLines, bulkConfirmDecision, { bulk: true })}
                                    className={bulkConfirmDecision === 'accepted'
                                        ? 'inline-flex h-8 items-center rounded-lg bg-[#0a8f53] px-3 text-[11px] font-black text-white transition hover:bg-[#087a43] disabled:cursor-wait disabled:opacity-60'
                                        : 'inline-flex h-8 items-center rounded-lg bg-[#b42318] px-3 text-[11px] font-black text-white transition hover:bg-[#931c13] disabled:cursor-wait disabled:opacity-60'}
                                >
                                    {isBulkUpdating ? 'Saving...' : bulkConfirmDecision === 'accepted' ? 'Yes, approve all' : 'Yes, decline all'}
                                </button>
                                <button
                                    type="button"
                                    disabled={isUpdating}
                                    onClick={() => setBulkConfirmDecision('')}
                                    className="inline-flex h-8 items-center rounded-lg border border-[#d8e6f3] bg-white px-3 text-[11px] font-black text-[#42566c] transition hover:bg-[#f8fafc] disabled:cursor-wait disabled:opacity-60"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                disabled={isUpdating}
                                onClick={() => { setNotice(''); setBulkConfirmDecision('accepted'); }}
                                className="inline-flex h-8 items-center rounded-lg bg-[#0a8f53] px-3 text-[11px] font-black text-white transition hover:bg-[#087a43] disabled:cursor-wait disabled:opacity-60"
                            >
                                Approve all
                            </button>
                            <button
                                type="button"
                                disabled={isUpdating}
                                onClick={() => { setNotice(''); setBulkConfirmDecision('declined'); }}
                                className="inline-flex h-8 items-center rounded-lg border border-[#ffd2d2] bg-white px-3 text-[11px] font-black text-[#b42318] transition hover:bg-[#fff1f1] disabled:cursor-wait disabled:opacity-60"
                            >
                                Decline all
                            </button>
                        </div>
                    )}
                </div>
            )}
            {estimateDeclined && (
                <div className="mt-4 rounded-2xl border border-[#ffd2d2] bg-white p-3">
                    <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#b42318]">Decline note</p>
                    <p className="mt-1 text-sm font-semibold leading-relaxed text-[#414141]">
                        {declineReasonText || 'No reason provided.'}
                    </p>
                </div>
            )}
            {notice && (
                <p className={`mt-3 rounded-xl px-3 py-2 text-sm font-bold ${/could not|already|not/i.test(notice) ? 'border border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]' : 'border border-[#bde7d5] bg-[#ecfff5] text-[#087a43]'}`}>
                    {notice}
                </p>
            )}
        </section>
    );
}

export default function GarageChatPage() {
    const navigate = useNavigate();
    const { requestId } = useParams();
    const { authState, openAuthModal, refreshDiagnosticTickets } = useOutletContext() || {};
    const [request, setRequest] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [pickupNotice, setPickupNotice] = useState('');
    const [isConfirmingPickup, setIsConfirmingPickup] = useState(false);
    // After pickup we ask the driver whether the issue is resolved: 'resolved' closes the
    // ticket, 'monitoring' leaves it under monitoring. Persisted per-request so the prompt
    // doesn't reappear once answered.
    const [pickupOutcome, setPickupOutcome] = useState('');
    const [isSavingOutcome, setIsSavingOutcome] = useState(false);
    const [outcomeNotice, setOutcomeNotice] = useState('');
    const [visitNotice, setVisitNotice] = useState('');
    const [isSendingVisitUpdate, setIsSendingVisitUpdate] = useState(false);
    const [selfRescheduleOpen, setSelfRescheduleOpen] = useState(false);

    const isAuthenticated = authState?.status === 'authenticated';
    const status = String(request?.request_status || '');
    const statusKey = status.toLowerCase();
    const estimateAccepted = request?.latest_estimate?.estimate_status === 'accepted' || ['estimate_accepted', 'scheduled', 'in_service', 'ready_for_pickup', 'picked_up', 'completed'].includes(statusKey);
    const appointmentScheduled = Boolean(request?.scheduled_start_at);
    const canScheduleAppointment = estimateAccepted && !appointmentScheduled && !['in_service', 'ready_for_pickup', 'picked_up', 'completed', 'declined', 'cancelled', 'failed'].includes(statusKey);
    const addToCalendarUrl = appointmentScheduled ? buildPartnerGoogleCalendarUrl(request) : '';
    const canConfirmPickup = statusKey === 'ready_for_pickup';
    const canRescheduleAppointment = canReschedulePartnerAppointment(request);
    const pendingRescheduleRequest = getPendingRescheduleRequest(request);
    const servicePickupCardStatuses = GARAGE_SERVICE_CARD_STATUSES.filter((serviceStatus) => (
        hasReachedGarageServiceStatus(statusKey, serviceStatus)
    ));
    const chatDisabled = TERMINAL_CHAT_STATUSES.has(statusKey);
    const canShowVisitReminder = shouldShowVisitReminder(request);
    const visitUpdateSent = hasDriverVisitUpdateMessage(request?.messages || [], request);
    const visitQuestionShouldStay = appointmentScheduled && (
        canShowVisitReminder ||
        visitUpdateSent ||
        ['in_service', 'ready_for_pickup', 'picked_up', 'completed'].includes(statusKey)
    );

    const loadRequest = useCallback(() => {
        if (!requestId || !isAuthenticated) return;
        getPartnerRepairRequest(requestId)
            .then((nextRequest) => {
                setRequest(nextRequest);
                setError('');
            })
            .catch((loadError) => {
                setError(loadError?.message || 'Could not load this shop conversation.');
            })
            .finally(() => setIsLoading(false));
    }, [isAuthenticated, requestId]);

    useEffect(() => {
        if (!isAuthenticated) {
            setIsLoading(false);
            return undefined;
        }
        setIsLoading(true);
        loadRequest();
        return undefined;
    }, [isAuthenticated, loadRequest]);

    const handlePartnerUpdate = useCallback(() => {
        loadRequest();
        // A shop-side status change (e.g. marking pickup) can move the linked ticket to
        // Monitoring — refresh so the sidebar reflects it instead of staying "Open".
        refreshDiagnosticTickets?.();
    }, [loadRequest, refreshDiagnosticTickets]);

    usePartnerRequestUpdates({
        diagnosticTicketId: request?.diagnostic_ticket_id,
        isAuthenticated,
        onUpdate: handlePartnerUpdate,
    });

    const updateRequest = (updated) => {
        if (updated) {
            setRequest(updated);
            if (!getPendingRescheduleRequest(updated)) {
                setSelfRescheduleOpen(false);
            }
        }
    };

    const handleDriverMessage = async (message) => {
        const updated = await sendPartnerRepairRequestMessage(request.request_id, message);
        updateRequest(updated);
        return updated;
    };

    const handleVisitConfirm = async () => {
        if (!request?.request_id || isSendingVisitUpdate || visitUpdateSent) return;
        setIsSendingVisitUpdate(true);
        setVisitNotice('');
        try {
            await handleDriverMessage(getDriverVisitUpdateMessage(request));
            if (String(request?.request_status || '').toLowerCase() === 'scheduled') {
                const updated = await updatePartnerRepairRequestStatus(request.request_id, 'in_service', 'Driver confirmed vehicle drop-off.');
                updateRequest(updated);
            }
            setVisitNotice('Vehicle marked in service.');
        } catch (visitError) {
            setVisitNotice(visitError?.message || 'Could not mark the vehicle in service.');
        } finally {
            setIsSendingVisitUpdate(false);
        }
    };

    const handlePickupConfirm = async () => {
        if (!request?.request_id || !canConfirmPickup) return;
        setIsConfirmingPickup(true);
        setPickupNotice('');
        try {
            const updated = await updatePartnerRepairRequestStatus(request.request_id, 'picked_up', 'Vehicle pickup confirmed by driver.');
            updateRequest(updated);
            setPickupNotice('Pickup confirmed.');
            // Pickup moves the linked ticket to Monitoring server-side — refresh the sidebar so
            // it stops showing "Open".
            refreshDiagnosticTickets?.();
        } catch (pickupError) {
            setPickupNotice(pickupError?.message || 'Could not confirm pickup.');
        } finally {
            setIsConfirmingPickup(false);
        }
    };

    // Restore a previously chosen post-pickup outcome so we don't re-ask on reload.
    useEffect(() => {
        if (!request?.request_id) return;
        try {
            const saved = window.localStorage.getItem(`carfido_pickup_outcome:${request.request_id}`);
            if (saved) setPickupOutcome(saved);
        } catch { /* ignore */ }
    }, [request?.request_id]);

    // Driver's answer to "Is your issue resolved?" after pickup:
    //   resolved  → close the linked diagnostic ticket (Resolved)
    //   not_sure  → leave it under monitoring (already set on pickup)
    const handlePickupOutcome = async (outcome) => {
        if (isSavingOutcome || !request?.request_id) return;
        setIsSavingOutcome(true);
        setOutcomeNotice('');
        try {
            if (outcome === 'resolved') {
                const ticketId = request?.diagnostic_ticket_id;
                if (ticketId && authState?.identity) {
                    const resp = await fetch(
                        `${getUserDetailsBaseUrl()}/api/diagnostic-tickets/${encodeURIComponent(authState.identity)}/${encodeURIComponent(ticketId)}`,
                        {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                status: 'closed',
                                stage: 'closed',
                                outcome: 'successful',
                                summary: 'Resolved after shop service and vehicle pickup.',
                            }),
                        },
                    );
                    if (!resp.ok) throw new Error('Could not close the ticket. Please try again.');
                }
                setPickupOutcome('resolved');
                setOutcomeNotice('Marked resolved — this case is now closed.');
            } else {
                // Not sure → keep monitoring AND arm the 3-day follow-up.
                tryEnableWebPushQuietly();
                const ticketId = request?.diagnostic_ticket_id;
                if (ticketId && authState?.identity) {
                    try {
                        await fetch(
                            `${getUserDetailsBaseUrl()}/api/diagnostic-tickets/${encodeURIComponent(authState.identity)}/${encodeURIComponent(ticketId)}/schedule-followup`,
                            { method: 'POST' },
                        );
                    } catch (schedErr) {
                        console.warn('[pickup outcome] schedule follow-up failed', schedErr);
                    }
                }
                setPickupOutcome('monitoring');
                setOutcomeNotice("Kept under monitoring — I'll check back in a few days.");
            }
            try {
                window.localStorage.setItem(
                    `carfido_pickup_outcome:${request.request_id}`,
                    outcome === 'resolved' ? 'resolved' : 'monitoring',
                );
            } catch { /* ignore */ }
            refreshDiagnosticTickets?.();
        } catch (outcomeError) {
            setOutcomeNotice(outcomeError?.message || 'Could not update the ticket.');
        } finally {
            setIsSavingOutcome(false);
        }
    };

    if (authState?.status === 'loading' || isLoading) {
        return (
            <div className="flex h-full w-full items-center justify-center bg-[#F9F9F9]">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#d9d9d9] border-t-[#D81F26]" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="flex h-full w-full items-center justify-center bg-[#F9F9F9] p-6 text-center">
                <div className="max-w-sm rounded-2xl border border-[#d9d9d9] bg-white p-6 shadow-sm">
                    <p className="text-xl font-black text-black">Sign in to view Garage chat</p>
                    <p className="mt-2 text-sm font-semibold text-[#414141]">Shop estimates and repair updates are linked to your phone number.</p>
                    <button
                        type="button"
                        onClick={openAuthModal}
                        className="mt-5 w-full rounded-xl bg-[#D81F26] px-5 py-3 text-sm font-black text-white hover:bg-[#B8181E]"
                    >
                        Sign in
                    </button>
                </div>
            </div>
        );
    }

    if (error || !request) {
        return (
            <div className="flex h-full w-full items-center justify-center bg-[#F9F9F9] p-6 text-center">
                <div className="max-w-sm rounded-2xl border border-[#d9d9d9] bg-white p-6 shadow-sm">
                    <p className="text-xl font-black text-black">Garage chat unavailable</p>
                    <p className="mt-2 text-sm font-semibold text-[#414141]">{error || 'This repair request could not be found.'}</p>
                    <button
                        type="button"
                        onClick={() => navigate('/garage')}
                        className="mt-5 w-full rounded-xl bg-[#D81F26] px-5 py-3 text-sm font-black text-white hover:bg-[#B8181E]"
                    >
                        Back to Garage
                    </button>
                </div>
            </div>
        );
    }

    return (
        <main className="h-full w-full overflow-y-auto bg-[#F9F9F9] text-[#414141] custom-scrollbar">
            <div className="mx-auto w-full max-w-5xl px-4 pb-12 pt-7 md:px-6 md:pt-9">
                <button
                    type="button"
                    onClick={() => navigate('/garage')}
                    className="mb-4 inline-flex min-h-[38px] items-center gap-1.5 rounded-full border border-[#d9d9d9] bg-white px-4 text-sm font-black text-[#414141] shadow-sm transition hover:border-[#414141] hover:bg-[#F9F9F9]"
                >
                    <Icon className="h-4 w-4"><path d="m15 18-6-6 6-6" /></Icon>
                    Back to Garage
                </button>

                <header className="mb-5 rounded-2xl border border-[#d9d9d9] bg-white p-5 shadow-sm">
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className={`rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.08em] ${getStatusTone(status)}`}>
                                    {getPartnerStatusLabel(status)}
                                </span>
                                <span className="text-xs font-bold text-[#414141]">
                                    {request.partner_shop?.name || 'Partner shop'}
                                </span>
                            </div>
                            <h1 className="mt-2 max-w-4xl text-xl font-black leading-snug text-black md:text-2xl">
                                {getRequestTitle(request)}
                            </h1>
                            <p className="mt-2 text-sm font-semibold text-[#414141]">
                                {getVehicleName(request)}
                            </p>
                        </div>
                        {request.partner_shop?.address && (
                            <a
                                href={`https://maps.google.com/?q=${encodeURIComponent(request.partner_shop.address)}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex min-h-[40px] shrink-0 items-center justify-center gap-2 rounded-xl border border-[#d9d9d9] bg-white px-4 text-sm font-black text-[#414141] transition hover:border-[#D81F26] hover:text-[#D81F26]"
                            >
                                <Icon className="h-4 w-4"><path d="M21 10c0 7-9 12-9 12S3 17 3 10a9 9 0 1 1 18 0z" /><circle cx="12" cy="10" r="3" /></Icon>
                                Directions
                            </a>
                        )}
                    </div>
                </header>

                <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
                    <div className="space-y-4">
                        <EstimateCard request={request} onUpdated={updateRequest} />

                        {canScheduleAppointment && (
                            <ScheduleAppointmentCard request={request} onScheduled={updateRequest} />
                        )}

                        {canRescheduleAppointment && (pendingRescheduleRequest || selfRescheduleOpen) && (
                            <RescheduleAppointmentCard
                                request={request}
                                rescheduleRequest={pendingRescheduleRequest}
                                onScheduled={updateRequest}
                                initialExpanded={selfRescheduleOpen}
                                onClose={pendingRescheduleRequest ? undefined : () => setSelfRescheduleOpen(false)}
                            />
                        )}

                        {visitQuestionShouldStay && (
                            <VisitReminderCard
                                request={request}
                                addToCalendarUrl={addToCalendarUrl}
                                onConfirmVisit={handleVisitConfirm}
                                isSubmitting={isSendingVisitUpdate}
                                visitUpdateSent={visitUpdateSent}
                                notice={visitNotice}
                                actionsEnabled={canShowVisitReminder}
                                onReschedule={canRescheduleAppointment ? () => setSelfRescheduleOpen(true) : undefined}
                            />
                        )}

                        {appointmentScheduled && !visitQuestionShouldStay && (
                            <section className="rounded-2xl border border-[#bde7d5] bg-[#ecfff5] p-4 shadow-sm">
                                <div className="flex items-start gap-3 text-[#087a43]">
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white">
                                        <Icon className="h-4 w-4"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18M8 15l2 2 5-5" /></Icon>
                                    </span>
                                    <div>
                                        <p className="text-xs font-black uppercase tracking-[0.08em]">Appointment confirmed</p>
                                        <p className="mt-1 text-sm font-black text-[#414141]">{formatPartnerScheduledAppointment(request)}</p>
                                        <p className="mt-1 text-xs font-semibold text-[#414141]">{request.partner_shop?.address || request.partner_shop?.name}</p>
                                        {(getCalendarStatus(request.calendar).label || addToCalendarUrl || request.calendar?.event_link || canRescheduleAppointment) && (
                                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                                {getCalendarStatus(request.calendar).label && (
                                                    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-black ${
                                                        getCalendarStatus(request.calendar).tone === 'success'
                                                            ? 'border-[#bde7d5] bg-white text-[#087a43]'
                                                            : getCalendarStatus(request.calendar).tone === 'danger'
                                                            ? 'border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]'
                                                            : 'border-[#d9d9d9] bg-white text-[#414141]'
                                                    }`}>
                                                        {getCalendarStatus(request.calendar).label}
                                                    </span>
                                                )}
                                                {addToCalendarUrl && (
                                                    <a
                                                        href={addToCalendarUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="inline-flex min-h-[28px] items-center rounded-full border border-[#d9d9d9] bg-white px-3 text-[11px] font-black text-[#414141] no-underline transition hover:border-[#D81F26] hover:text-[#D81F26]"
                                                    >
                                                        Add to my Google Calendar
                                                    </a>
                                                )}
                                                {request.calendar?.event_link && (
                                                    <a
                                                        href={request.calendar.event_link}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="inline-flex min-h-[28px] items-center rounded-full border border-[#bde7d5] bg-white px-3 text-[11px] font-black text-[#087a43] no-underline"
                                                    >
                                                        Open synced event
                                                    </a>
                                                )}
                                                {canRescheduleAppointment && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelfRescheduleOpen(true)}
                                                        className="inline-flex min-h-[28px] items-center rounded-full border border-[#f7d58f] bg-white px-3 text-[11px] font-black text-[#936000]"
                                                    >
                                                        Reschedule
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </section>
                        )}

                        {servicePickupCardStatuses.map((serviceStatus) => (
                            <ServicePickupCard
                                key={`service-pickup-${request.request_id}-${serviceStatus}`}
                                request={request}
                                onConfirmPickup={handlePickupConfirm}
                                isSubmitting={isConfirmingPickup}
                                notice={serviceStatus === statusKey ? pickupNotice : ''}
                                statusOverride={serviceStatus}
                                actionsEnabled={serviceStatus === statusKey}
                            />
                        ))}

                        {['picked_up', 'completed'].includes(statusKey) && (
                            <section className="rounded-2xl border border-[#bde7d5] bg-[#ecfff5] p-4 shadow-sm">
                                <p className="text-base font-black text-[#087a43]">
                                    {statusKey === 'completed' ? 'Service completed' : 'Vehicle picked up'}
                                </p>
                                {pickupOutcome === 'resolved' ? (
                                    <p className="mt-1 text-sm font-semibold text-[#414141]">Your issue is marked resolved and this case is now closed.</p>
                                ) : pickupOutcome === 'monitoring' ? (
                                    <>
                                        <p className="mt-1 text-sm font-semibold text-[#414141]">We're keeping this issue under monitoring. Once you're confident it's fixed, mark it resolved anytime.</p>
                                        <div className="mt-3">
                                            <button
                                                type="button"
                                                onClick={() => handlePickupOutcome('resolved')}
                                                disabled={isSavingOutcome}
                                                className="inline-flex min-h-[34px] items-center justify-center rounded-full bg-[#0a8f53] px-4 text-xs font-black text-white transition hover:bg-[#087a43] disabled:cursor-wait disabled:opacity-60"
                                            >
                                                {isSavingOutcome ? 'Saving...' : "It's fixed — mark resolved"}
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <p className="mt-2 text-sm font-black text-[#414141]">Is your issue resolved?</p>
                                        <p className="mt-1 text-sm font-semibold text-[#414141]">Confirm so we can close this case, or keep it under monitoring if you're not sure yet.</p>
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                onClick={() => handlePickupOutcome('resolved')}
                                                disabled={isSavingOutcome}
                                                className="inline-flex min-h-[34px] items-center justify-center rounded-full bg-[#0a8f53] px-4 text-xs font-black text-white transition hover:bg-[#087a43] disabled:cursor-wait disabled:opacity-60"
                                            >
                                                {isSavingOutcome ? 'Saving...' : "Yes, it's fixed"}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handlePickupOutcome('not_sure')}
                                                disabled={isSavingOutcome}
                                                className="inline-flex min-h-[34px] items-center justify-center rounded-full border border-[#bde7d5] bg-white px-4 text-xs font-black text-[#087a43] transition hover:bg-[#f0fff7] disabled:cursor-wait disabled:opacity-60"
                                            >
                                                Not sure — keep monitoring
                                            </button>
                                        </div>
                                    </>
                                )}
                                {outcomeNotice && <p className="mt-2 text-xs font-bold text-[#0a8f53]">{outcomeNotice}</p>}
                            </section>
                        )}

                        <GarageProgressConversation
                            requests={[request]}
                            includeMessages
                            includeWaiting={false}
                            showRequestHeader={false}
                        />

                        <PartnerRequestMessages
                            messages={request.messages || []}
                            currentRole="driver"
                            title="Message shop"
                            emptyText="No messages with this shop yet."
                            placeholder={chatDisabled ? 'This shop conversation is closed.' : 'Message the shop...'}
                            onSend={handleDriverMessage}
                            disabled={chatDisabled}
                            showMessageList={false}
                        />
                    </div>

                    <aside className="space-y-4">
                        <section className="rounded-2xl border border-[#d9d9d9] bg-white p-4 shadow-sm">
                            <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#414141]">Shop-ready summary</p>
                            <p className="mt-3 text-sm font-semibold leading-relaxed text-[#414141]">
                                {request.shop_ready_summary?.customerComplaint || request.services?.[0]?.summary || 'No report summary available.'}
                            </p>
                            <div className="mt-4 space-y-3">
                                <div>
                                    <p className="text-xs font-black text-[#414141]">Likely causes</p>
                                    <ul className="mt-2 space-y-1.5">
                                        {(request.shop_ready_summary?.likelyCauses || []).slice(0, 4).map((item) => (
                                            <li key={item} className="text-sm font-semibold text-[#414141]">- {item}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <p className="text-xs font-black text-[#414141]">Requested tests</p>
                                    <ul className="mt-2 space-y-1.5">
                                        {(request.shop_ready_summary?.requestedTests || []).slice(0, 4).map((item) => (
                                            <li key={item} className="text-sm font-semibold text-[#414141]">- {item}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </section>
                    </aside>
                </div>
            </div>
        </main>
    );
}
